import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Divider from "@material-ui/core/Divider";
import Grow from "@material-ui/core/Grow";
import Hidden from "@material-ui/core/Hidden";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Paper from "@material-ui/core/Paper";
import Poppers from "@material-ui/core/Popper";
import { makeStyles } from "@material-ui/core/styles";
import Notifications from "@material-ui/icons/Notifications";
import axios from "axios";
import classNames from "classnames";
import React from "react";
import { useHistory } from "react-router-dom";
import styles from "../../../assets/jss/material-dashboard-react/components/headerLinksStyle";
import { api } from "../../../config";
import Button from "../CustomButtons/Button";

const useStyles = makeStyles(styles as any);

// bigger count badge than the template default (9px text, 16px circle)
const useBadgeStyles = makeStyles((theme) => ({
  badge: {
    [theme.breakpoints.up("md")]: {
      fontSize: "12px",
      fontWeight: 600,
      minWidth: "22px",
      height: "22px",
      lineHeight: "20px",
      borderRadius: "11px",
      padding: "0 4px",
      top: "-4px",
      right: "-2px",
    },
  },
}));

// re-check for discounts entering the 2-day window while the panel stays open
const REFRESH_MS = 5 * 60 * 1000;

interface DiscountNotification {
  id: number;
  name: string;
  end: string;
  read: boolean;
}

const timeLeft = (end: string) => {
  const hours = Math.max(
    0,
    Math.floor((new Date(end).getTime() - Date.now()) / 3600000)
  );
  if (hours < 1) return "less than an hour";
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return `${days} day${days === 1 ? "" : "s"}${
    rest ? ` ${rest} hour${rest === 1 ? "" : "s"}` : ""
  }`;
};

const DiscountNotifications: React.FC = () => {
  const classes = useStyles();
  const badgeClasses = useBadgeStyles();
  const history = useHistory();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [notifications, setNotifications] = React.useState<
    DiscountNotification[]
  >([]);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const fetchNotifications = React.useCallback(async () => {
    try {
      const res = await axios.get(`${api}/discount-notifications`, {
        withCredentials: true,
      });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unreadCount);
    } catch (err) {
      console.log(err);
    }
  }, []);

  React.useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (anchor) {
      setAnchor(null);
    } else {
      setAnchor(event.currentTarget);
      fetchNotifications();
    }
  };

  const handleOpen = async (notification: DiscountNotification) => {
    setAnchor(null);
    if (!notification.read) {
      try {
        await axios.post(
          `${api}/discount-notifications/${notification.id}/read`,
          {},
          { withCredentials: true }
        );
      } catch (err) {
        console.log(err);
      }
      fetchNotifications();
    }
    history.push(`/admin/discount-form/${notification.id}`);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(
        `${api}/discount-notifications/read-all`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      console.log(err);
    }
    fetchNotifications();
  };

  return (
    <div className={classes.manager}>
      <Button
        aria-owns={anchor ? "notification-menu-list-grow" : null}
        aria-haspopup="true"
        //@ts-ignore
        onClick={handleToggle}
        className={classes.buttonLink}
      >
        <Notifications className={classes.icons} />
        {unreadCount > 0 && (
          <span
            className={classNames(classes.notifications, badgeClasses.badge)}
          >
            {unreadCount}
          </span>
        )}
        <Hidden mdUp implementation="css">
          <p className={classes.linkText}>Notifications</p>
        </Hidden>
      </Button>
      <Poppers
        open={Boolean(anchor)}
        anchorEl={anchor}
        transition
        disablePortal
        placement="bottom-end"
        className={
          classNames({ [classes.popperClose]: !anchor }) +
          " " +
          classes.popperNav
        }
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            //@ts-ignore
            id="notification-menu-list-grow"
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={() => setAnchor(null)}>
                <MenuList role="menu" style={{ width: 340, maxWidth: "90vw" }}>
                  <MenuItem
                    disabled
                    className={classes.dropdownItem}
                    style={{ fontWeight: 500, opacity: 1 }}
                  >
                    Discounts expiring soon
                  </MenuItem>
                  {unreadCount > 0 && (
                    <MenuItem
                      onClick={handleMarkAllRead}
                      className={classes.dropdownItem}
                    >
                      Mark all as read
                    </MenuItem>
                  )}
                  <Divider light />
                  {notifications.length === 0 && (
                    <MenuItem disabled className={classes.dropdownItem}>
                      No notifications
                    </MenuItem>
                  )}
                  {notifications.map((notification) => (
                    <MenuItem
                      key={notification.id}
                      onClick={() => handleOpen(notification)}
                      className={classes.dropdownItem}
                      style={{
                        whiteSpace: "normal",
                        fontWeight: notification.read ? 400 : 600,
                        opacity: notification.read ? 0.7 : 1,
                      }}
                    >
                      {!notification.read && "● "}"{notification.name}" expires
                      in {timeLeft(notification.end)}
                      <br />
                      <small>{new Date(notification.end).toLocaleString()}</small>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Poppers>
    </div>
  );
};

export default DiscountNotifications;
